/*
 * Copyright 2026 Sony Corporation
 */

import {
  DEFAULT_LENS_ID,
  GENERIC_BODY,
  bodyOf,
  defaultOptics,
  lensOf,
  type BodySpec,
  type FormatSpec,
  type LensSpec,
  type Optics,
} from '@ips-device-verse/twin-service/schema';

/**
 * A device's lens and sensor, and everything that follows from them
 * (camera-simulation.md §5).
 *
 * The rig's frustum lines and the preview camera both read these, which is what
 * makes the wireframe in the viewport and the picture in the inspector agree by
 * construction rather than by luck. Pure arithmetic: no Babylon in this file.
 */
export interface ResolvedOptics {
  /** Always valid for `body` and `lens`: out-of-range stored values are pulled back in. */
  optics: Optics;
  body: BodySpec;
  /** False when the model is not in the catalog and a generic body stands in. */
  knownBody: boolean;
  lens: LensSpec;
  format: FormatSpec;
  /** The part of the sensor the format records, in millimetres. */
  sensor: { width: number; height: number };
}

const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max);

const recordedArea = (body: BodySpec, format: FormatSpec): ResolvedOptics['sensor'] => {
  const crop = format.crop ?? 1;
  const width = body.sensor_mm.width / crop;
  const height = body.sensor_mm.height / crop;
  const aspect = format.width_px / format.height_px;
  // A wider format keeps the full width and loses height; a taller one the reverse.
  return aspect >= width / height ? { width, height: width / aspect } : { width: height * aspect, height };
};

export const resolveOptics = (model: string, stored?: Optics): ResolvedOptics => {
  const body = bodyOf(model);
  const base = stored ?? defaultOptics(model);
  const lens = lensOf(base.lens_id) ?? lensOf(DEFAULT_LENS_ID)!;
  const format = body.formats.find((candidate) => candidate.id === base.format_id) ?? body.formats[0];

  return {
    optics: {
      lens_id: lens.id,
      focal_length_mm: clamp(base.focal_length_mm, lens.focal_mm.min, lens.focal_mm.max),
      aperture_f: clamp(base.aperture_f, lens.aperture.widest, lens.aperture.narrowest),
      focus_distance_m: Math.max(base.focus_distance_m, lens.min_focus_m),
      format_id: format.id,
    },
    body,
    knownBody: body !== GENERIC_BODY,
    lens,
    format,
    sensor: recordedArea(body, format),
  };
};

/** What a rig with no device behind it (an assistant's proposal) is drawn with. */
export const DEFAULT_OPTICS: ResolvedOptics = resolveOptics(GENERIC_BODY.model);

/** Full angles of view, in radians. */
export const fieldOfView = ({ sensor, optics }: ResolvedOptics) => {
  const angle = (size: number) => 2 * Math.atan(size / (2 * optics.focal_length_mm));
  return {
    horizontal: angle(sensor.width),
    vertical: angle(sensor.height),
    diagonal: angle(Math.hypot(sensor.width, sensor.height)),
  };
};

export const aspectOf = ({ sensor }: ResolvedOptics): number => sensor.width / sensor.height;

/** The usual d/1500 criterion, on the recorded area's diagonal, in millimetres. */
export const circleOfConfusion = ({ sensor }: ResolvedOptics): number => Math.hypot(sensor.width, sensor.height) / 1500;

/** Near and far limits of acceptable sharpness, in metres; `far` is Infinity past the hyperfocal distance. */
export const depthOfField = (resolved: ResolvedOptics) => {
  const f = resolved.optics.focal_length_mm;
  const n = resolved.optics.aperture_f;
  const s = resolved.optics.focus_distance_m * 1000;
  const hyperfocal = (f * f) / (n * circleOfConfusion(resolved)) + f;

  return {
    near: (s * (hyperfocal - f)) / (hyperfocal + s - 2 * f) / 1000,
    far: s >= hyperfocal ? Infinity : (s * (hyperfocal - f)) / (hyperfocal - s) / 1000,
    hyperfocal: hyperfocal / 1000,
  };
};

/** How much of a plane `distance` metres away the frame covers, in metres. */
export const footprint = ({ sensor, optics }: ResolvedOptics, distance: number) => ({
  width: (distance * sensor.width) / optics.focal_length_mm,
  height: (distance * sensor.height) / optics.focal_length_mm,
});

/** Millimetres of subject per recorded pixel, at `distance` metres. */
export const pixelPitch = (resolved: ResolvedOptics, distance: number): number =>
  (footprint(resolved, distance).width * 1000) / resolved.format.width_px;

const NAMED_ASPECTS: readonly [number, number][] = [
  [1, 1],
  [5, 4],
  [4, 3],
  [3, 2],
  [16, 9],
  [17, 9],
  [2, 1],
];

/** "16:9" rather than "1.78", when the ratio is one anyone would name. */
export const aspectLabel = (width: number, height: number): string => {
  const ratio = width / height;
  const named = NAMED_ASPECTS.find(([w, h]) => Math.abs(w / h - ratio) / ratio < 0.01);
  return named ? `${named[0]}:${named[1]}` : `${ratio.toFixed(2)}:1`;
};
